import { Activity, ArrowLeft, Clock3, HardDrive, MapPin, Network, Radio } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { ProblemList } from "../components/ProblemList";
import { Card, DataAge, ErrorState, LoadingBlock, StatusBadge } from "../components/ui";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useRemote } from "../hooks/useRemote";
import { apiRequest } from "../lib/api";
import { CRITICALITY_LABELS, formatDateTime, HEALTH_LABELS } from "../lib/format";
import type { BuildingDetail, BuildingHistory, HealthState } from "../types";

export function BuildingDetailPage() {
  const { id = "" } = useParams();
  const detail = useRemote(() => apiRequest<BuildingDetail>(`/api/v1/buildings/${encodeURIComponent(id)}`), [id], 30_000);
  const history = useRemote(() => apiRequest<BuildingHistory>(`/api/v1/buildings/${encodeURIComponent(id)}/history`), [id], 60_000);
  useDocumentTitle(detail.data?.name ?? "Edificio");

  if (detail.loading && !detail.data) return <div className="page"><LoadingBlock rows={8} /></div>;
  if (detail.error && !detail.data) return <div className="page"><ErrorState error={detail.error} onRetry={detail.reload} /></div>;
  if (!detail.data) return null;
  const building = detail.data;

  return (
    <div className="page">
      <Link className="back-link" to="/edificios"><ArrowLeft size={16} /> Volver a edificios</Link>
      <header className="building-hero">
        <div className="building-hero__identity">
          <span className={`building-hero__icon building-hero__icon--${building.status}`}><Radio size={28} /></span>
          <div><p className="eyebrow">{building.client.name} · {building.region.name}</p><h1>{building.name}</h1><p><MapPin size={15} /> {building.address || building.city} <span>·</span> Código {building.externalId}</p></div>
        </div>
        <div className="building-hero__state"><StatusBadge status={building.status} /><span>{statusExplanation(building.status)}</span><DataAge value={building.lastSeenAt} prefix="Último dato" /></div>
      </header>

      <section className="device-summary" aria-label="Estado de los componentes">
        <Card className="device-card"><span className="device-card__icon"><Network size={21} /></span><div><span>Conectividad</span><StatusBadge status={building.networkStatus} compact /></div><small>Acceso al edificio</small></Card>
        <Card className="device-card"><span className="device-card__icon"><HardDrive size={21} /></span><div><span>Grabador</span><StatusBadge status={building.recorderStatus} compact /></div><small>DVR / NVR principal</small></Card>
      </section>

      <section className="building-detail-layout">
        <div className="building-detail-main">
          <Card>
            <div className="card-heading"><div><p className="eyebrow">Situación actual</p><h2>Problemas abiertos</h2></div><span className="count-badge">{building.problems.length}</span></div>
            <ProblemList problems={building.problems} compact />
          </Card>
        </div>
        <aside className="building-detail-side">
          <Card>
            <div className="card-heading"><div><p className="eyebrow">Últimas 24 horas</p><h2>Historia de estado</h2></div><Activity size={21} /></div>
            {history.loading && !history.data ? <LoadingBlock rows={3} /> : null}
            {history.data?.statusIntervals.length ? <div className="status-history">{history.data.statusIntervals.slice(0, 8).map((interval, index) => <div className="status-history__item" key={`${interval.startedAt}-${index}`}><span className={`status-history__line status-history__line--${interval.status}`} /><div><strong>{HEALTH_LABELS[interval.status]}</strong><small>{formatDateTime(interval.startedAt)}{interval.endedAt ? ` → ${formatDateTime(interval.endedAt)}` : " · actual"}</small>{interval.reason ?? interval.reasonCode ? <span>{interval.reason ?? humanizeReason(interval.reasonCode)}</span> : null}</div></div>)}</div> : null}
            {history.data && history.data.statusIntervals.length === 0 ? <p className="empty-copy">Todavía no hay cambios de estado en este período.</p> : null}
            {history.error && !history.data ? <ErrorState error={history.error} onRetry={history.reload} /> : null}
          </Card>
          <Card>
            <div className="card-heading"><div><p className="eyebrow">Información</p><h2>Datos del edificio</h2></div><Clock3 size={21} /></div>
            <dl className="info-list"><div><dt>Criticidad</dt><dd>{CRITICALITY_LABELS[building.criticality]}</dd></div><div><dt>Zona horaria</dt><dd>{building.timezone}</dd></div><div><dt>Problemas activos</dt><dd>{building.openProblems}</dd></div><div><dt>Último contacto</dt><dd><DataAge value={building.lastSeenAt} /></dd></div></dl>
          </Card>
        </aside>
      </section>
    </div>
  );
}

function humanizeReason(reason: string | null | undefined): string {
  if (!reason) return "";
  return reason.replace(/_/g, " ").toLocaleLowerCase("es");
}

function statusExplanation(status: HealthState): string {
  switch (status) {
    case "operational": return "La conectividad y el grabador informan normalmente.";
    case "degraded": return "La fuente informó un estado que necesita revisión.";
    case "outage": return "La conectividad o el grabador están fuera de servicio.";
    case "stale": return "No recibimos información reciente de este edificio.";
    case "maintenance": return "El edificio está dentro de una ventana de mantenimiento.";
  }
}
